// The answer to an agent's proposal to move a date: accept the proposed date, keep the one
// in force, or (an action's) move it to another. Applied at once, then sent to the agent
// to take in. Shown on the action it moves, or under Agent feedback for a tracked date.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import { useAnswerFeedbackQuestion } from "../api/hooks/useAgentFeedback";
import { formatDay, isoDay } from "../utils/attributeFormat";
import { changeSaved, useSentNotice } from "../state/sentNotice";
import { PushControl } from "./ActionControls";
import styles from "./NextActionTable.module.css";

export const DateMoveAnswer = ({
  questionId,
  current,
  proposed,
  pushAllowed,
}: {
  questionId: string;
  current: string;
  proposed: string;
  pushAllowed: boolean;
}) => {
  const { user } = useKindeAuth();
  const answer = useAnswerFeedbackQuestion();
  const showSent = useSentNotice();
  const send = (body: { options: number[]; date?: string }) =>
    answer.mutate(
      {
        question_id: questionId,
        other: "",
        actor_email: user?.email ?? "",
        // Applied at once; the agent takes it in on its next daily run, as a push from the
        // row does.
        wake: false,
        ...body,
      },
      { onSuccess: () => showSent(changeSaved()) },
    );
  return (
    <>
      <div className={styles.buttons}>
        <button
          type="button"
          className={`${styles.button} ${styles.primary}`}
          disabled={answer.isLoading}
          onClick={() => send({ options: [0] })}
        >
          Accept {formatDay(proposed)}
        </button>
        <button
          type="button"
          className={styles.button}
          disabled={answer.isLoading}
          onClick={() => send({ options: [1] })}
        >
          Keep {formatDay(current)}
        </button>
        {pushAllowed && (
          <PushControl
            target={{
              scope: "one",
              due: current,
              onPush: (date) => send({ options: [], date }),
            }}
            today={isoDay(new Date())}
            busy={answer.isLoading}
          />
        )}
      </div>
      {answer.error && <p className={styles.error}>{answer.error.message}</p>}
    </>
  );
};
